import { useCallback, useMemo } from 'react';
import {
  TABLE_GRID_HEADER_HEIGHT,
  TABLE_ROW_MEASURE_HEIGHT,
} from '@/features/shared/components/tableSystem/tableConstants';
import { DataGrid } from '../../primitives/DataGrid';
import {
  buildSkeletonDataGridColumns,
  skeletonDataGridBaseSx,
  type LoadingRow,
} from './SkeletonDataGridSupport';
import { SkeletonPaginationFooter } from './SkeletonPaginationFooter';
import type { SpacingToken } from '@/theme/tokens';
import type {
  DataGridProps,
  GridColDef,
  GridPaginationModel,
  GridRowId,
  GridValidRowModel,
  SxProps,
  Theme,
} from '@/ui/primitives';
import type { ReactElement } from 'react';

type SkeletonDataGridProps<T extends GridValidRowModel> = {
  columns: Array<GridColDef<T>>;
  loadingRowCount?: number;
  totalRowCount?: number;
  pageSizeOptions?: number[];
  pageSize?: number;
  paginationModel?: GridPaginationModel;
  rowHeight?: number;
  autoHeight?: boolean;
  showToolbar?: boolean;
  getRowId: (row: T) => GridRowId;
  getRowHeight?: DataGridProps['getRowHeight'];
  rowHeights?: number[];
  cellPaddingY?: SpacingToken;
  columnVisibilityModel?: DataGridProps['columnVisibilityModel'];
  slotProps?: DataGridProps['slotProps'];
  localeText?: DataGridProps['localeText'];
  sx?: DataGridProps['sx'];
  hideFooter?: boolean;
};

type SkeletonRowHeightSx = Record<string, unknown>;

const buildSkeletonRowHeightSx = (args: {
  loadingRowCount: number;
  rowHeight?: number;
  rowHeights?: number[];
}): SxProps<Theme> | null => {
  const resolvedRowHeights = Array.from({ length: args.loadingRowCount }, (_value, index) => {
    return args.rowHeights?.[index] ?? args.rowHeight;
  });
  const numericRowHeights = resolvedRowHeights.filter(
    (height): height is number => typeof height === 'number',
  );

  if (numericRowHeights.length !== resolvedRowHeights.length) {
    return null;
  }

  const rowHeightSx: SkeletonRowHeightSx = {
    '&& .MuiDataGrid-virtualScrollerContent': {
      flexBasis: `${numericRowHeights.reduce((sum, height) => sum + height, 0)}px !important`,
    },
  };

  numericRowHeights.forEach((height, index) => {
    const heightPx = `${height}px`;
    rowHeightSx[`&& .MuiDataGrid-row[data-id="loading-${String(index)}"]`] = {
      '--height': heightPx,
      minHeight: `${heightPx} !important`,
      maxHeight: `${heightPx} !important`,
      height: `${heightPx} !important`,
      '& .MuiDataGrid-cell': {
        '--height': heightPx,
        minHeight: `${heightPx} !important`,
        maxHeight: `${heightPx} !important`,
        height: `${heightPx} !important`,
      },
    };
  });

  return rowHeightSx as SxProps<Theme>;
};

export const SkeletonDataGrid = <T extends GridValidRowModel>({
  columns,
  loadingRowCount = 10,
  totalRowCount,
  pageSizeOptions = [5, 10, 25, 50],
  pageSize,
  paginationModel,
  rowHeight,
  autoHeight = false,
  showToolbar = false,
  getRowId,
  getRowHeight,
  rowHeights,
  cellPaddingY,
  columnVisibilityModel,
  slotProps,
  localeText,
  sx,
  hideFooter = false,
}: SkeletonDataGridProps<T>): ReactElement => {
  const resolvedSkeletonRowHeight = rowHeight ?? TABLE_ROW_MEASURE_HEIGHT;
  const hasMeasuredRowHeights = rowHeights !== undefined && rowHeights.length > 0;
  const hasFixedSkeletonRowHeight =
    typeof resolvedSkeletonRowHeight === 'number' || hasMeasuredRowHeights;
  const resolvedGetRowHeight: DataGridProps['getRowHeight'] | undefined = hasMeasuredRowHeights
    ? (params) => {
        const rowId = String(params.id);
        const rowIndex = rowId.startsWith('loading-')
          ? Number.parseInt(rowId.replace('loading-', ''), 10)
          : Number.NaN;
        const measuredHeight = Number.isFinite(rowIndex) ? rowHeights[rowIndex] : undefined;
        return measuredHeight ?? resolvedSkeletonRowHeight;
      }
    : hasFixedSkeletonRowHeight
      ? undefined
      : (getRowHeight ?? undefined);
  const resolvedRowHeight =
    hasMeasuredRowHeights || !hasFixedSkeletonRowHeight ? undefined : resolvedSkeletonRowHeight;
  const resolvedPageSize =
    pageSize && pageSizeOptions.includes(pageSize) ? pageSize : (pageSizeOptions[0] ?? 5);

  const loadingRows = useMemo<LoadingRow[]>(
    () =>
      Array.from({ length: loadingRowCount }, (_value, index) => ({
        id: `loading-${index}`,
        __loading: true,
      })),
    [loadingRowCount],
  );

  const columnsWithLoading = useMemo(() => {
    return buildSkeletonDataGridColumns({
      columns,
      cellPaddingY,
      rowHeight: resolvedSkeletonRowHeight,
      rowHeights,
    });
  }, [cellPaddingY, columns, resolvedSkeletonRowHeight, rowHeights]);

  const resolveRowId = useCallback(
    (row: T) => {
      if ('__loading' in row) {
        return (row as unknown as LoadingRow).id;
      }
      return getRowId(row);
    },
    [getRowId],
  );

  const resolvedPaginationModel = paginationModel ?? { page: 0, pageSize: resolvedPageSize };
  const resolvedRowCount = typeof totalRowCount === 'number' ? totalRowCount : loadingRows.length;
  const skeletonRowHeightSx = buildSkeletonRowHeightSx({
    loadingRowCount,
    rowHeight: resolvedSkeletonRowHeight,
    rowHeights,
  });
  const resolvedSx: SxProps<Theme> = [
    skeletonDataGridBaseSx,
    ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
    skeletonRowHeightSx,
  ].filter(Boolean) as SxProps<Theme>;

  return (
    <>
      <DataGrid
        rows={loadingRows as unknown as T[]}
        columns={columnsWithLoading}
        disableRowSelectionOnClick
        disableColumnMenu
        hideFooterSelectedRowCount
        hideFooter
        pageSizeOptions={pageSizeOptions}
        paginationMode="server"
        paginationModel={resolvedPaginationModel}
        rowCount={resolvedRowCount}
        getRowId={resolveRowId}
        initialState={{ pagination: { paginationModel: resolvedPaginationModel } }}
        autoHeight={autoHeight}
        columnHeaderHeight={TABLE_GRID_HEADER_HEIGHT}
        rowHeight={resolvedRowHeight}
        getRowHeight={resolvedGetRowHeight}
        showToolbar={showToolbar}
        columnVisibilityModel={columnVisibilityModel}
        slotProps={slotProps}
        localeText={localeText}
        sx={resolvedSx}
      />
      {hideFooter ? null : <SkeletonPaginationFooter />}
    </>
  );
};
